import React from 'react';
import type { BusinessCard } from '../lib/types';

interface CardPreviewProps {
  card: BusinessCard;
  onClick?: () => void;
  className?: string;
}

export function CardPreview({ card, onClick, className = '' }: CardPreviewProps) {
  const isPending = card.status === 'pending';

  const expires = card.expiresAt ? new Date(card.expiresAt) : null;
  const isExpired = expires ? expires < new Date() : false;

  return (
    <div
      className={`card-preview relative overflow-hidden rounded-xl border border-white/10 transition-transform duration-200 hover:-translate-y-1 hover:shadow-2xl ${isPending ? 'animate-pulse' : ''} ${isExpired ? 'opacity-60' : ''} ${className}`}
      onClick={onClick}
    >
      <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_25%_30%,rgba(255,255,255,0.28),transparent_35%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.18),transparent_30%)]" />
      <div className={`relative z-10 flex h-full flex-col justify-between gap-4 p-6 text-white ${isPending ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h3 className="text-2xl font-semibold tracking-tight drop-shadow-sm">{card.name}</h3>
            {card.title && <p className="text-sm font-medium opacity-90">{card.title}</p>}
            {card.company && <p className="text-xs uppercase tracking-[0.25em] opacity-75">{card.company}</p>}
          </div>
          <div className="flex items-center gap-2">
            {isPending && <Chip tone="cool">Pending</Chip>}
            {isExpired && <Chip tone="warm">Expired</Chip>}
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {card.email && <ContactInfo icon="✉️" text={card.email} />}
          {card.phone && <ContactInfo icon="📱" text={card.phone} />}
          {card.location.address && <ContactInfo icon="📍" text={card.location.address} />}
        </div>
        
        <div className="mt-2 flex items-center justify-between gap-4 text-xs">
          <div className="flex flex-wrap gap-2">
            {card.social?.linkedin && <SocialLink href={card.social.linkedin} icon="in" />}
            {card.social?.twitter && <SocialLink href={card.social.twitter} icon="x" />}
            {card.social?.github && <SocialLink href={card.social.github} icon="gh" />}
          </div>
          {expires && (
            <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-wide text-[10px]">
              <span className="h-2 w-2 rounded-full bg-amber-300" />
              Expires {expires.toLocaleDateString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactInfo({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-lg">{icon}</span>
      <span className="truncate">{text}</span>
    </div>
  );
}

function SocialLink({ href, icon }: { href: string; icon: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex h-8 w-8 items-center justify-center rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold uppercase tracking-wide transition-all hover:-translate-y-0.5 hover:bg-white/25"
      onClick={(e) => e.stopPropagation()}
    >
      {icon}
    </a>
  );
}

function Chip({ children, tone }: { children: React.ReactNode; tone: 'cool' | 'warm' }) {
  const toneClasses =
    tone === 'cool'
      ? 'bg-white/20 border-white/30 text-white'
      : 'bg-amber-100/80 text-amber-900 border-amber-300/80';
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${toneClasses}`}>
      <span className="h-2 w-2 rounded-full bg-current" />
      {children}
    </span>
  );
}
